import type { MarketplaceService } from '../../types/pricing';

export type GuidedModePath = 'standard_package' | 'help_me_choose';

export interface GuidedModeAnswers {
  path: GuidedModePath;
  supported_users: number;
  term_months: number;
  internal_it_owner?: 'yes' | 'no' | 'partial';
  productivity_platform?: string;
  security_baseline?: string;
  support_pain?: string[];
}

export interface GuidedModeRecommendation {
  outcome: 'signature_support_standard_package' | 'needs_scoping_review' | 'consider_block_hours';
  title: string;
  summary: string;
  variantKey?: string;
  quantity: number;
  termMonths: number;
  monthlyCents?: number;
  reasons: string[];
  serviceConfiguration: Record<string, any>;
}

function asRecord(value: unknown): Record<string, any> {
  return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, any> : {};
}

function getOrderSchema(service: MarketplaceService): Record<string, any> {
  return asRecord((service as any).order_schema);
}

export function isGuidedCheckoutService(service: MarketplaceService): boolean {
  return getOrderSchema(service).mode === 'guided_checkout';
}

function getStandardPackage(service: MarketplaceService): Record<string, any> {
  return asRecord(asRecord(getOrderSchema(service).valid_options).standard_package);
}

function getGuidedQuestions(service: MarketplaceService): any[] {
  const questions = asRecord(getOrderSchema(service).valid_options).guided_questions;
  return Array.isArray(questions) ? questions : [];
}

export function getGuidedModeDefaults(service: MarketplaceService): GuidedModeAnswers {
  const standardPackage = getStandardPackage(service);
  return {
    path: 'standard_package',
    supported_users: Number(standardPackage.default_seats || 25),
    term_months: Number(standardPackage.default_term_months || service.default_term_months || 36),
    productivity_platform: 'microsoft_365',
    internal_it_owner: 'partial',
    security_baseline: 'managed',
    support_pain: [],
  };
}

function findVariantKey(service: MarketplaceService, seats: number, termMonths: number): string | undefined {
  const variants = service.variants?.variants || [];
  const termText = `${termMonths}`;
  const tier = variants.find((variant: any) => {
    const key = String(variant.key || variant.variant_key || '').toLowerCase();
    const label = `${variant.display_name || ''} ${variant.attribute1_value || ''} ${variant.attribute2_value || ''}`.toLowerCase();
    const matchesTerm = key.includes(termText) || label.includes(termText);
    if (!matchesTerm) return false;
    if (seats <= 25) return key.includes('1-25') || label.includes('1-25');
    if (seats <= 50) return key.includes('26-50') || label.includes('26-50');
    return key.includes('51-100') || label.includes('51-100');
  });
  return tier?.variant_key || tier?.key || service.default_variant_key;
}

function monthlyCentsFor(service: MarketplaceService, variantKey: string | undefined, seats: number): number | undefined {
  if (!variantKey) return undefined;
  const variant = (service.variants?.variants || []).find((entry: any) => (entry.variant_key || entry.key) === variantKey);
  const recurring = Number(variant?.pricing?.recurring_cents || 0);
  return recurring > 0 ? recurring * seats : undefined;
}

export function evaluateGuidedMode(service: MarketplaceService, answers: GuidedModeAnswers): GuidedModeRecommendation {
  const seats = Math.max(1, Math.floor(Number(answers.supported_users || 1)));
  const termMonths = Number(answers.term_months || service.default_term_months || 36);
  const reasons: string[] = [];

  let outcome: GuidedModeRecommendation['outcome'] = 'signature_support_standard_package';
  let title = 'Signature Support recommended';
  let summary = 'Use Ntiva Signature Support as the managed IT operating model, then confirm environment details after checkout.';

  if (seats > 100) {
    outcome = 'needs_scoping_review';
    title = 'Needs scoping review';
    summary = 'This looks larger than the published package range. Capture the request, then finalize the managed-services design with Ntiva before supplier submission.';
    reasons.push('Supported user count is above the published 100-user package range.');
  } else if (answers.internal_it_owner === 'yes' && (answers.support_pain || []).length <= 1) {
    outcome = 'consider_block_hours';
    title = 'Consider Block Hours first';
    summary = 'The buyer may already have enough internal IT ownership for a reserved-capacity support model. Signature Support still works if they want Ntiva to own day-to-day operations.';
    reasons.push('Buyer reports an internal IT owner and limited pain points.');
  } else {
    reasons.push('Buyer needs a recurring managed IT operating model rather than one-off support.');
  }

  if (answers.security_baseline === 'basic' || answers.security_baseline === 'not_sure') {
    reasons.push('Security baseline suggests managed endpoint, identity, and backup controls are valuable.');
  }
  if ((answers.support_pain || []).includes('unclear_ownership')) {
    reasons.push('Unclear ownership is a strong fit for one accountable MSP.');
  }

  const variantKey = findVariantKey(service, seats, termMonths);
  const monthlyCents = monthlyCentsFor(service, variantKey, seats);

  return {
    outcome,
    title,
    summary,
    variantKey,
    quantity: seats,
    termMonths,
    monthlyCents,
    reasons,
    serviceConfiguration: {
      guided_mode: {
        entry: answers.path,
        outcome,
        supported_users: seats,
        term_months: termMonths,
        internal_it_owner: answers.internal_it_owner,
        productivity_platform: answers.productivity_platform,
        security_baseline: answers.security_baseline,
        support_pain: answers.support_pain || [],
        recommendation_title: title,
        recommendation_summary: summary,
      },
    },
  };
}

export function getGuidedQuestionsForDisplay(service: MarketplaceService): any[] {
  return getGuidedQuestions(service);
}
